import useSWR from 'swr';
import { taskSnapshotMetadataZod } from 'packages/lib/api/annotations/contract/task-snapshot';
import { useNonNullQueryParams } from 'packages/lib/utils/hooks/use-query';
import { ZodTypeDef, ZodType, z } from 'zod';
import { apiGet } from 'packages/lib/api/annotations/rest';

/** A snapshot is a task's Annotation History. A new 'snapshot' is taken of this history each time it transitions between certain states. */
export const useTaskSnapshotSWR = <T, Z extends ZodTypeDef = ZodTypeDef>(
  taskToken: string | undefined,
  zodType: ZodType<T, Z>
) => {
  const params = new URLSearchParams();
  params.set('taskToken', taskToken ?? '');
  const zod = z.array(
    taskSnapshotMetadataZod.extend({
      snapshot: zodType,
    })
  );
  return useSWR<z.infer<typeof zod>>(
    taskToken ? `/task-snapshots?${params.toString()}` : null,
    async (url: string) => {
      const snapshots = await apiGet(url, zod.parse);
      return snapshots.map((snapshot) => ({
        ...snapshot,
        taskToken: snapshot.taskToken ?? taskToken,
      }));
    }
  );
};

export const useCurrentTaskSnapshot = <T, Z extends ZodTypeDef = ZodTypeDef>(
  taskToken: string | undefined,
  zodType: ZodType<T, Z>
) => {
  const urlParamsObj = useNonNullQueryParams<{
    snapshotToken: string | undefined;
  }>();
  const {
    data: taskSnapshots,
    isLoading: isLoadingTaskSnapshots,
    error: taskSnapshotError,
  } = useTaskSnapshotSWR(taskToken, zodType);
  const snapshot = taskSnapshots?.find(
    (snapshot) => snapshot.token === urlParamsObj.snapshotToken
  );
  return { isLoadingTaskSnapshots, taskSnapshotError, snapshot };
};
